import React from "react";
import PropTypes from "prop-types";
import { FieldRepMacCard, UserInformation, LoginActivity } from "components/User";
import { FILTER_TYPE_NONE, FORMATTER_RIGHT_ALIGN } from "components/Shared/MGrid.tsx";
import { dateTimeToStringWithNumOfDays } from "components/Shared/Formatters";
import { GridCard, MCard, MSpinner, MGridLayout } from "components/Shared";
import {
  getFRAddressInformationCard,
  getFRContactInformationCard,
  getFRRemarksCard,
  getFRHRInformationCard,
  getFROtherInformationCard,
  getFRDetailsCard,
  getFRDesignationsCard,
} from "typeConstants/mCardDefinitions/fieldRepProfile";
import CaseIssues from "components/Homeowner/FieldReps/CaseIssues";
import * as employeeRoles from "typeConstants/employeeRoles";
import { getEmployeesByRoles } from "requests/employee";
import ExclusionaryList from "components/Shared/ExclusionaryList";
import EmployeeCalendar from "components/Employee/EmployeeCalendar.tsx";
import AdminTimeLog from "components/Shared/AdminTimeLog";
import EmployeeTEAM from "components/Employee/EmployeeTEAM";
import FieldRepServiceRegion from "components/Homeowner/FieldReps/FieldRepServiceRegion";
import InspectorRegionManager from "components/InspectorRegionManager/index.tsx";
import EmployeeCourses from "components/Employee/EmployeeCourses";
import { userHasRight } from "selectors/rights";
import { EMPLOYEE_COMPLAINT_VIEW } from "typeConstants/rights";
import { connect } from "react-redux";
import EmployeeComplaintCard from "components/Employee/EmployeeComplaintCard.tsx";

let colDefs = [
  {
    key: "inspectionType",
    name: "Survey Type",
    width: 300,
    dataSource: "inspectionType",
    filterType: FILTER_TYPE_NONE,
  },
  {
    key: "customerName",
    name: "Mueller Customer",
    width: 300,
    dataSource: "customerName",
    filterType: FILTER_TYPE_NONE,
  },
  { key: "fullName", name: "Insured's Name", width: 200, dataSource: "fullName", filterType: FILTER_TYPE_NONE },
  { key: "customerDueDate", name: "Due", width: 200, dataSource: "customerDueDate", filterType: FILTER_TYPE_NONE },
  { key: "dateAssigned", name: "Assigned", width: 200, dataSource: "dateAssigned", filterType: FILTER_TYPE_NONE },
  {
    key: "fieldAppointmentDate",
    name: "Appointment",
    width: 200,
    dataSource: "fieldAppointmentDate",
    filterType: FILTER_TYPE_NONE,
  },
  { key: "controlNumber", name: "Control #", width: 100, dataSource: "controlNumber", filterType: FILTER_TYPE_NONE },
  {
    key: "mac",
    name: "MAC",
    width: 80,
    dataSource: "mac",
    filterType: FILTER_TYPE_NONE,
    formatter: FORMATTER_RIGHT_ALIGN,
  },
  {
    key: "percentageEnhanced",
    name: "% Enhanced",
    width: 100,
    dataSource: "percentageEnhanced",
    filterType: FILTER_TYPE_NONE,
  },
];

class FieldRepProfile extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      canViewNotes: props.canViewNotes,
      fieldLevelCoordinators: [],
      fieldRepDevelopmentManagers: [],
      isEditingRegions: false,
    };
    this.saveFieldRepChanges = this.saveFieldRepChanges.bind(this);
    this.viewOrdersRedirect = this.viewOrdersRedirect.bind(this);
    this.getHeaderDefinitions = this.getHeaderDefinitions.bind(this);
    this.mounted = { current: true };
  }

  componentDidMount() {
    const {
      location,
      frNumberOverride,
      history,
      getInspectorDetails,
      getAssignedCases,
      getUser,
      stateList,
      getAllTeamLeaders,
      updateHeader,
    } = this.props;
    let frNum = this.getFRNumber();
    let internetUserIdOverride = this.getInternetUserId();

    if (!location.state && !frNumberOverride) {
      history.push({ pathname: "/" });
    } else {
      if (frNum) {
        getInspectorDetails(frNum);
        getAssignedCases(frNum);
      }
      if (internetUserIdOverride) getUser(internetUserIdOverride);
    }

    if (!stateList || stateList.length == 0) this.props.getStateList();
    if (!this.state.fieldLevelCoordinators || this.state.fieldLevelCoordinators.length == 0)
      this.getFieldLevelCoordinators();
    if (!this.fieldRepDevelopmentManagers || !this.fieldRepDevelopmentManagers.length)
      this.getFieldDevelopmentManagers();

    getAllTeamLeaders();
    updateHeader(this.getHeaderDefinitions());
  }

  componentWillUnmount() {
    this.mounted.current = false;
    this.props.clearInternetUser();
    this.props.clearHeader();
  }

  UNSAFE_componentWillUpdate(nextProps) {
    if (
      this.props.frNumberOverride != nextProps.frNumberOverride ||
      this.props.internetUserIdOverride != nextProps.internetUserIdOverride
    ) {
      this.props.getInspectorDetails(nextProps.frNumberOverride);
      this.props.getAssignedCases(nextProps.frNumberOverride);
      this.props.getUser(nextProps.internetUserIdOverride);
    }
  }

  getFieldLevelCoordinators() {
    getEmployeesByRoles(
      [employeeRoles.FIELD_OPERATIONS_MANAGER],
      (data) => this.setState({ fieldLevelCoordinators: data }),
      this.mounted,
    );
  }

  getFieldDevelopmentManagers() {
    getEmployeesByRoles(
      [employeeRoles.FIELD_DEVELOPMENT_MANAGER],
      (data) => this.setState({ fieldRepDevelopmentManagers: data }),
      this.mounted,
    );
  }

  getFRNumber() {
    return this.props.frNumberOverride || (this.props.location.state && this.props.location.state.frNumber);
  }

  getInternetUserId() {
    return this.props.internetUserIdOverride || (this.props.location.state && this.props.location.state.internetUserId);
  }

  saveFieldRepChanges(keyValues) {
    const { frNumberOverride, location } = this.props;
    let frNumber = frNumberOverride || location.state.frNumber;
    // must reverse value for disableAutoAssign field before saving since display is its inverse
    let autoAssignIndex = keyValues.findIndex((obj) => obj.key === "disableAutoAssign");
    if (autoAssignIndex !== -1) {
      let originalVal = keyValues[autoAssignIndex].originalValue;
      keyValues[autoAssignIndex].originalValue = keyValues[autoAssignIndex].value;
      keyValues[autoAssignIndex].value = originalVal;
    }
    this.props.updateFieldRep(frNumber, keyValues, () => this.props.getInspectorDetails(frNumber));
  }

  getHeaderDefinitions() {
    return {
      dropdownTitle: "Field Rep Profile",
      buttons: [
        {
          name: "View Order Queue",
          onClick: this.viewOrdersRedirect,
        },
      ],
    };
  }

  viewOrdersRedirect() {
    this.props.history.push({
      pathname: "/inspector-cases",
      state: {
        inspectorId: this.props.fieldRep.inspectorLocation.id,
      },
    });
  }

  render() {
    let isLoading =
      this.props.isFetching ||
      !this.props.fieldRep ||
      this.props.isFetchingInternetUser ||
      Object.keys(this.props.internetUser).length === 0 ||
      this.props.isFetchingAssignedCases ||
      !this.props.assignedCases ||
      !this.props.stateList ||
      this.props.isFetchingAllTeamLeaders;

    if (isLoading) return <MSpinner />;
    const { fieldRep, internetUser, stateList, allTeamLeaders, canEditHrInfo, canEditCalendarAsManager } = this.props;

    let addressInfo = getFRAddressInformationCard(
      fieldRep,
      stateList.map((s) => ({ label: s.stateName, value: s.stateAbbreviation })),
    );
    let contactInfo = getFRContactInformationCard(fieldRep);
    let remarks = getFRRemarksCard(fieldRep);
    let hrInfo = getFRHRInformationCard(fieldRep);
    let otherInfo = getFROtherInformationCard(
      fieldRep,
      allTeamLeaders,
      this.state.fieldLevelCoordinators,
      this.state.fieldRepDevelopmentManagers,
      this.props.canEditCanBeAssignedCases,
      this.props.canDisableAutoAssign,
      this.props.canEditProfile,
    );
    let details = getFRDetailsCard(fieldRep);
    let designations = getFRDesignationsCard({
      fieldRep,
      onEditFinished: this.saveFieldRepChanges,
      userRights: this.props.rights,
    });

    let gridClassname = "field-rep-profile";
    if (this.state.isEditingRegions) gridClassname += " hidden";

    return (
      <>
        {this.state.isEditingRegions && (
          <InspectorRegionManager
            inspectorNumber={fieldRep.inspectorLocation.inspectorNumber}
            onClose={() => this.setState({ isEditingRegions: false })}
          />
        )}
        <MGridLayout
          className={gridClassname}
          verticalDisplay={this.props.verticalDisplay}
          spacing={1}
          padding={1}
          rows={[
            // Row 1: Address, Contact, Remarks
            [
              {
                component: (
                  <MCard
                    title={addressInfo.title}
                    isEditable
                    onEditFinished={this.saveFieldRepChanges}
                    fields={addressInfo.fields}
                  />
                ),
                stretchHeight: true,
              },
              {
                component: (
                  <MCard
                    title={contactInfo.title}
                    isEditable
                    onEditFinished={this.saveFieldRepChanges}
                    fields={contactInfo.fields}
                  />
                ),
                stretchHeight: true,
              },
              {
                component: (
                  <MCard
                    title={remarks.title}
                    isEditable
                    onEditFinished={this.saveFieldRepChanges}
                    fields={remarks.fields}
                  />
                ),
                stretchHeight: true,
              },
            ],
            // Row 2: Calendar, TEAM
            [
              fieldRep.employeeId && {
                component: (
                  <MCard
                    title="Employee Calendar"
                    fields={[
                      {
                        key: "calendar",
                        control: (
                          <EmployeeCalendar
                            employeeId={fieldRep.employeeId}
                            readOnly={canEditCalendarAsManager ? false : true}
                            allowManagementEdit={canEditCalendarAsManager}
                          />
                        ),
                      },
                    ]}
                  />
                ),
                size: 6,
                stretchHeight: true,
              },
              this.state.canViewNotes && {
                component: <EmployeeTEAM internetUserId={this.getInternetUserId()} overrideCellWidth />,
                size: 6,
                stretchHeight: true,
              },
            ].filter(Boolean),
            // Row 3: FR Feedback, Field Rep Coverage
            [
              this.props.canViewEmployeeComplaints && {
                component: <EmployeeComplaintCard employeeId={fieldRep.employeeId} />,
                size: 6,
                stretchHeight: true,
              },
              {
                component: (
                  <FieldRepServiceRegion
                    onEditClicked={() => this.setState({ isEditingRegions: true })}
                    isEditing={this.state.isEditingRegions}
                    overrideCellWidth
                  />
                ),
                size: 6,
                stretchHeight: true,
              },
            ].filter(Boolean),
            // Row 4: HR Info, Other Info, Details
            [
              {
                component: (
                  <MCard
                    title={hrInfo.title}
                    fields={hrInfo.fields}
                    isEditable={canEditHrInfo}
                    onEditFinished={this.saveFieldRepChanges}
                  />
                ),
                stretchHeight: true,
              },
              {
                component: (
                  <MCard
                    title={otherInfo.title}
                    fields={otherInfo.fields}
                    isEditable
                    onEditFinished={this.saveFieldRepChanges}
                  />
                ),
                stretchHeight: true,
              },
              { component: <MCard title={details.title} fields={details.fields} />, stretchHeight: true },
            ],
            // Row 5: Exclusionary List, Designations, User Info
            [
              {
                component: (
                  <MCard
                    title="Exclusionary List"
                    fields={[
                      {
                        key: "exclusions",
                        control: <ExclusionaryList inspectorNumber={fieldRep.inspectorLocation.inspectorNumber} />,
                      },
                    ]}
                  />
                ),
                size: 4,
                stretchHeight: true,
              },
              (this.props.canViewFrDesignations || this.props.canEditFrDesignations) && {
                component: <MCard {...designations} isEditable={this.props.canEditFrDesignations} />,
                size: 4,
                stretchHeight: true,
              },
              {
                component: (
                  <UserInformation
                    userInformation={internetUser}
                    internetUserId={internetUser.id}
                    canEditUser={internetUser.canEditUser}
                  />
                ),
                size: 4,
                stretchHeight: true,
              },
            ].filter(Boolean),
            // Row 6: Login Activity, MAC Log
            [
              {
                component: <LoginActivity loginStatistics={internetUser.loginStatistics} />,
                size: 6,
                stretchHeight: true,
              },
              {
                component: (
                  <div className="mac-list-cell md-grid-cards">
                    <FieldRepMacCard inspectorNumber={fieldRep.inspectorLocation.inspectorNumber} />
                  </div>
                ),
                size: 6,
                stretchHeight: true,
              },
            ],
            // Row 7: Training and Misc Log
            [
              {
                component: (
                  <div className="md-grid-cards">
                    <AdminTimeLog
                      inspectorNumber={fieldRep.inspectorLocation.inspectorNumber}
                      canAddAndEdit={this.props.canEditAdminTimeLog}
                    />
                  </div>
                ),
                size: 12,
                stretchHeight: true,
              },
            ],
            // Row 8: Case Issues
            [{ component: <CaseIssues frNumber={this.getFRNumber()} />, size: 12, stretchHeight: true }],
            // Row 9: Field Rep Queue
            [
              {
                component: (
                  <div className="field-rep-queue md-grid-cards card-style">
                    <GridCard
                      gridCols={colDefs}
                      headerText="Field Rep Queue"
                      data={this.props.assignedCases.map((x) => ({
                        inspectionType: x.inspectionType,
                        customerName: x.customerName,
                        fullName: x.insuredFirstName + " " + x.insuredLastName,
                        customerDueDate: x.caseLocation.customerDueDate
                          ? dateTimeToStringWithNumOfDays(x.caseLocation.customerDueDate)
                          : "n/a",
                        dateAssigned: x.dateAssigned ? dateTimeToStringWithNumOfDays(x.dateAssigned) : "n/a",
                        controlNumber: x.caseLocation.caseId,
                        mac: "$" + x.mac,
                        percentageEnhanced: x.caseLocation.frIncentivePercent,
                        fieldAppointmentDate: x.fieldAppointmentDate
                          ? dateTimeToStringWithNumOfDays(x.fieldAppointmentDate)
                          : "n/a",
                      }))}
                    />
                  </div>
                ),
                size: 12,
                stretchHeight: true,
              },
            ],
            // Row 10: Employee Courses (conditional)
            fieldRep.employeeId && this.props.canViewEmployeeCourses
              ? [{ component: <EmployeeCourses employeeId={fieldRep.employeeId} />, size: 12, stretchHeight: true }]
              : [],
          ].filter((row) => row.length > 0)}
        />
      </>
    );
  }
}

FieldRepProfile.propTypes = {
  frNumberOverride: PropTypes.number,
  internetUserIdOverride: PropTypes.number,
  getInspectorDetails: PropTypes.func.isRequired,
  updateFieldRep: PropTypes.func.isRequired,
  getStateList: PropTypes.func.isRequired,
  stateList: PropTypes.array,
  isFetching: PropTypes.bool.isRequired,
  fieldRep: PropTypes.object,
  location: PropTypes.object,
  history: PropTypes.object,
  getUser: PropTypes.func.isRequired,
  isFetchingInternetUser: PropTypes.bool.isRequired,
  internetUser: PropTypes.object.isRequired,
  verticalDisplay: PropTypes.bool,
  clearInternetUser: PropTypes.func.isRequired,
  getAssignedCases: PropTypes.func.isRequired,
  isFetchingAssignedCases: PropTypes.bool.isRequired,
  assignedCases: PropTypes.array,
  allTeamLeaders: PropTypes.array.isRequired,
  getAllTeamLeaders: PropTypes.func.isRequired,
  isFetchingAllTeamLeaders: PropTypes.bool.isRequired,
  getNewSearchResults: PropTypes.func,
  updateHeader: PropTypes.func,
  clearHeader: PropTypes.func,
  canEditHrInfo: PropTypes.bool, // Allows HR Info card to be editable
  makeToast: PropTypes.func,
  canEditFrDesignations: PropTypes.bool,
  canViewFrDesignations: PropTypes.bool,
  canViewNotes: PropTypes.bool,
  canEditCalendarAsManager: PropTypes.bool,
  canEditAdminTimeLog: PropTypes.bool,
  canEditCanBeAssignedCases: PropTypes.bool,
  canDisableAutoAssign: PropTypes.bool,
  canViewEmployeeCourses: PropTypes.bool,
  rights: PropTypes.arrayOf(PropTypes.string),
  canViewEmployeeComplaints: PropTypes.bool,
  canEditProfile: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  canViewEmployeeComplaints: userHasRight(EMPLOYEE_COMPLAINT_VIEW)(state),
});

export default connect(mapStateToProps, null)(FieldRepProfile);
